import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Select from "react-select";
import {
  classList,
  sectionList,
  subjectList,
  syllabusCategoryList,
} from "../../utils";
import { fetchSyllabus } from "../../redux/syllabusSlice";
import { fetchSamplePapers } from "../../redux/samplePaperSlice";
import { server } from "../../server";
import DownloadResult from "../../components/DownloadResult";
import DownloadAnnualResult from "../../components/DownloadAnnualResult";
import DownloadResultXLS from "../../components/result/DownloadResultXLS";
import DownloadAnnualResultXLS from "../../components/result/DownloadAnnualResultXLS";
import DownloadAdmitCard from "../../components/DownloadAdmitCard";

/**
 * ExaminationReports
 *
 * Mirrors the StudentReports layout exactly:
 *  Left col  → Hero + Dataset selector + Filters + Live Snapshot
 *  Right col → Report panel (category tabs + report buttons + quick action cards)
 *
 * Datasets:
 *  1. Marks         – student exam marks (ExaminationMarks)
 *  2. Results       – compiled results   (Result)
 *  3. Toppers       – class/section/subject toppers (Toppers)
 *  4. Admit Cards   – admit-card records (AdmitCard)
 *  5. Syllabus      – syllabus documents (Syllabus)
 *  6. Sample Papers – sample paper docs  (SamplePaper)
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const toUpper = (v) =>
  v !== null && v !== undefined ? String(v).toUpperCase() : "";

const today = new Date().toISOString().split("T")[0];

const writeWorkbook = (wb, filename) => {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/octet-stream" }), filename);
  toast.success("Report Generated");
};

const sortByRoll = (arr) =>
  arr.sort((a, b) => Number(a["ROLL NO"] || 0) - Number(b["ROLL NO"] || 0));

// ─── dataset meta ─────────────────────────────────────────────────────────────
const DATASETS = {
  marks: { label: "Exam Marks", color: "success" },
  results: { label: "Results", color: "warning" },
  toppers: { label: "Toppers", color: "danger" },
  admitcards: { label: "Admit Cards", color: "info" },
  syllabus: { label: "Syllabus", color: "warning" },
  samplepapers: { label: "Sample Papers", color: "info" },
};

// ─── report categories ────────────────────────────────────────────────────────
const REPORT_CATEGORIES = [
  { key: "all", label: "All Reports" },
  { key: "marks", label: "Marks" },
  { key: "results", label: "Results" },
  { key: "toppers", label: "Toppers" },
  { key: "admitcards", label: "Admit Cards" },
  { key: "academic", label: "Academic Docs" },
  { key: "analytics", label: "Analytics" },
];

// ─── exam types used across the app ──────────────────────────────────────────
const EXAM_TYPES = ["half yearly", "annual"];
const SESSION = "2024-2025";

// ─── Modal sub-components (same pattern as Result.js) ────────────────────────

const monthList = [
  "All",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
  "january",
  "february",
  "march",
];

const ClassSectionPicker = ({
  selectedClass,
  selectedSection,
  setSelectedClass,
  setSelectedSection,
  selectedMonth,
  setSelectedMonth,
  showMonth = false,
  onDownload,
  isDownloading,
}) => (
  <>
    <div className="mt-4">
      <div className="bg-light rounded-pill py-2 mb-3">
        <h5 className="fs-5 text-center m-0 lh-1 fw-medium">Select Class</h5>
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
        {classList.map((cls, i) => (
          <div
            key={i}
            className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
              selectedClass === cls
                ? "bg-warning text-white border-warning"
                : ""
            }`}
            onClick={() => setSelectedClass(cls)}
          >
            {cls}
          </div>
        ))}
      </div>
    </div>

    <div className="mt-4">
      <div className="bg-light rounded-pill py-2 mb-3">
        <h5 className="fs-5 text-center m-0 lh-1 fw-medium">Select Section</h5>
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-1 px-lg-8 px-5 mx-lg-4">
        {sectionList.map((sec, i) => (
          <div
            key={i}
            className={`bg-dark-soft rounded-pill cursor-pointer px-2 py-1 fs-6 ${
              selectedSection === sec
                ? "bg-warning text-white border-warning"
                : ""
            }`}
            onClick={() => setSelectedSection(sec)}
          >
            <span className="mx-1">{sec}</span>
          </div>
        ))}
      </div>
    </div>

    {showMonth && setSelectedMonth && (
      <div className="mt-4">
        <div className="bg-light rounded-pill py-2 mb-3">
          <h5 className="fs-5 text-center m-0 lh-1 fw-medium">Select Month</h5>
        </div>
        <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
          {monthList.map((m, i) => (
            <div
              key={i}
              className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                selectedMonth === m
                  ? "bg-warning text-white border-warning"
                  : ""
              }`}
              onClick={() => setSelectedMonth(m)}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
      {isDownloading ? (
        <div className="bg-primary md-circle-button fw-medium">
          <div
            className="spinner-border spinner-border-md text-white"
            role="status"
          ></div>
        </div>
      ) : (
        <button
          onClick={onDownload}
          className="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
        >
          Download
        </button>
      )}
    </div>
  </>
);

const ExamModal = ({
  id,
  title,
  subtitle,
  icon = "bi-file-earmark-pdf-fill",
  onDownload,
  isDownloading,
  selectedClass,
  selectedSection,
  setSelectedClass,
  setSelectedSection,
  selectedMonth,
  setSelectedMonth,
  showMonth = false,
}) => (
  <div className="modal m-0 fade" id={id} tabIndex="-1" aria-hidden="true">
    <div className="modal-dialog modal-dialog-centered">
      <div className="modal-content rounded-5 overflow-hidden">
        <div className="p-4 bg-danger-soft d-flex justify-content-end">
          <i
            className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></i>
        </div>
        <div className="pt-0 p-5">
          <div className="d-flex justify-content-center mt-n8">
            <img
              src="/assets/images/report.jpg"
              width="90"
              className="object-fit-contain p-3 rounded-5 bg-white"
            />
          </div>
          <div className="mt-2">
            <h5 className="mb-1 text-center">{title}</h5>
            <h5 className="text-center lh-1 fw-medium">
              Class / Section Wise
              <br />
              <span className="text-secondary">{subtitle}</span>
            </h5>
          </div>
          <ClassSectionPicker
            selectedClass={selectedClass}
            selectedSection={selectedSection}
            setSelectedClass={setSelectedClass}
            setSelectedSection={setSelectedSection}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            showMonth={showMonth}
            onDownload={onDownload}
            isDownloading={isDownloading}
          />
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
function ExaminationReports() {
  const dispatch = useDispatch();

  // ── redux slices (syllabus + sample papers already fetched via redux) ──────
  const { data: syllabus, loading: loadingSyllabus } = useSelector(
    (s) => s.syllabus,
  );
  const { data: samplePapers, loading: loadingSamplePapers } = useSelector(
    (s) => s.samplePaper,
  );

  // ── local data states ──────────────────────────────────────────────────────
  const [marksData, setMarksData] = useState([]);
  const [resultsData, setResultsData] = useState([]);
  const [toppersData, setToppersData] = useState([]);
  const [admitCardsData, setAdmitCardsData] = useState([]);

  const [loadingMarks, setLoadingMarks] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingToppers, setLoadingToppers] = useState(false);
  const [loadingAdmitCards, setLoadingAdmitCards] = useState(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeDataset, setActiveDataset] = useState("marks");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("half yearly");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── PDF download state ─────────────────────────────────────────────────────
  const [isDownloading, setIsDownloading] = useState(false);
  const [modalClass, setModalClass] = useState("Uday");
  const [modalSection, setModalSection] = useState("B");
  const [modalMonth, setModalMonth] = useState("All");

  // ── shared date normaliser (same as Result.js) ────────────────────────────
  const normalizeDate = (dateStr) => {
    const [day, month, year] = dateStr.includes("/")
      ? dateStr.split("/")
      : dateStr.split("-");
    return new Date(+year, +month - 1, +day);
  };

  // ── shared result data fetcher ─────────────────────────────────────────────
  const fetchResultData = async (examFromDate, examToDate) => {
    const marksRes = await fetch(
      `${server}/GetStudentMarksThroughFilter?session=${SESSION}&examType=half%20yearly&class=${modalClass}&section=${modalSection}`,
      { headers: { Authorization: localStorage.getItem("token") } },
    );
    const marksData = await marksRes.json();

    const headsRes = await fetch(
      `${server}/GetResultHeadsOfParticularSessionAndClass?session=${SESSION}&class=${modalClass}`,
      { headers: { Authorization: localStorage.getItem("token") } },
    );
    const headsData = await headsRes.json();

    const attendanceRes = await fetch(`${server}/search_attendance_mongodb`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
      },
      body: JSON.stringify({
        Class: modalClass,
        Section: modalSection,
        "From Date": examFromDate.toLocaleDateString("en-GB"),
        "To Date": examToDate.toLocaleDateString("en-GB"),
      }),
    });
    const attendanceJson = await attendanceRes.json();
    const attendanceData = (attendanceJson?.["body-json"]?.body || []).map(
      (s, i) => ({
        ...s,
        rollNo: s.rollNo || i + 1,
        name: s.name,
      }),
    );

    const holidayRes = await fetch(
      `${server}/GetAllDefaultHolidays?session=${SESSION}`,
      {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json",
        },
      },
    );
    const holidayJson = await holidayRes.json();
    return { marksData, headsData, attendanceData, holidayJson };
  };

  // ── Half Yearly PDF ───────────────────────────────────────────────────────
  const handleDownloadHalfYearlyPDF = async () => {
    if (!modalClass || !modalSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      const from = new Date(2025, 3, 3);
      const to = new Date(2025, 7, 31);
      const { marksData, headsData, attendanceData, holidayJson } =
        await fetchResultData(from, to);
      const holidayData = (holidayJson?.holidays || []).map((h) => ({
        ...h,
        date: normalizeDate(h.date),
      }));
      const totalDays = Math.floor((to - from) / (1000 * 60 * 60 * 24)) + 1;
      const classHolidays = holidayData.filter(
        (h) => h.date >= from && h.date <= to && h.class.includes(modalClass),
      );
      const workingDays = totalDays - classHolidays.length;
      await DownloadResult({
        marks: marksData,
        heads: headsData,
        attendanceData,
        holidayData,
        selectedClass: modalClass,
        selectedSection: modalSection,
        totalDays,
        workingDays,
      });
      setModalClass("");
      setModalSection("");
    } catch {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Half Yearly XLS ───────────────────────────────────────────────────────
  const handleDownloadHalfYearlyXLS = async () => {
    if (!modalClass || !modalSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      const from = new Date(2025, 3, 3);
      const to = new Date(2025, 7, 31);
      const { marksData, headsData, attendanceData, holidayJson } =
        await fetchResultData(from, to);
      const holidayData = (holidayJson?.holidays || []).map((h) => ({
        ...h,
        date: normalizeDate(h.date),
      }));
      const classHolidays = holidayData.filter(
        (h) => h.date >= from && h.date <= to && h.class.includes(modalClass),
      );
      const workingDays =
        Math.floor((to - from) / (1000 * 60 * 60 * 24)) +
        1 -
        classHolidays.length;
      await DownloadResultXLS({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass: modalClass,
        selectedSection: modalSection,
        workingDays,
      });
      setModalClass("");
      setModalSection("");
    } catch {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Annual PDF ─────────────────────────────────────────────────────────────
  const handleDownloadAnnualPDF = async () => {
    if (!modalClass || !modalSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      const from = new Date(2025, 3, 1);
      const to = new Date(2026, 2, 31);
      const { marksData, headsData, attendanceData, holidayJson } =
        await fetchResultData(from, to);
      const holidays = (holidayJson?.holidays || []).map((h) => {
        const [year, month, day] = h.date.split("-");
        return { ...h, date: `${day}/${month}/${year}` };
      });
      const holidayDatesSet = new Set(
        holidays
          .filter((h) => h.class.includes(modalClass))
          .map((h) => {
            const [dd, mm, yyyy] = h.date.split("/").map(Number);
            return new Date(yyyy, mm - 1, dd).toDateString();
          }),
      );
      let workingDays = 0;
      const cursor = new Date(from);
      while (cursor <= to) {
        if (!holidayDatesSet.has(cursor.toDateString())) workingDays++;
        cursor.setDate(cursor.getDate() + 1);
      }
      await DownloadAnnualResult({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass: modalClass,
        selectedSection: modalSection,
        workingDays,
        holidays,
      });
      setModalClass("");
      setModalSection("");
    } catch (e) {
      console.error(e);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Annual XLS ────────────────────────────────────────────────────────────
  const handleDownloadAnnualXLS = async () => {
    if (!modalClass || !modalSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      const from = new Date(2025, 3, 1);
      const to = new Date(2026, 2, 31);
      const { marksData, headsData, attendanceData, holidayJson } =
        await fetchResultData(from, to);
      const holidays = (holidayJson?.holidays || []).map((h) => {
        const [year, month, day] = h.date.split("-");
        return { ...h, date: `${day}/${month}/${year}` };
      });
      const holidayDatesSet = new Set(
        holidays
          .filter((h) => h.class.includes(modalClass))
          .map((h) => {
            const [dd, mm, yyyy] = h.date.split("/").map(Number);
            return new Date(yyyy, mm - 1, dd).toDateString();
          }),
      );
      let workingDays = 0;
      const cursor = new Date(from);
      while (cursor <= to) {
        if (!holidayDatesSet.has(cursor.toDateString())) workingDays++;
        cursor.setDate(cursor.getDate() + 1);
      }
      await DownloadAnnualResultXLS({
        marks: marksData,
        heads: headsData,
        attendanceData,
        selectedClass: modalClass,
        selectedSection: modalSection,
        workingDays,
        holidays,
      });
      setModalClass("");
      setModalSection("");
    } catch (e) {
      console.error(e);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Admit Card — Class Wise PDF ───────────────────────────────────────────
  const handleDownloadAdmitCardClassWise = async () => {
    if (!modalClass) {
      toast.error("Select Class", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      let url = `${server}/GetAdmitCardByClassAndSection?class=${modalClass}`;
      if (modalMonth !== "All") url += `&month=${modalMonth}`;
      const res = await fetch(url);
      const data = await res.json();
      const students = (data?.allStudents || []).sort(
        (a, b) =>
          Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
      );
      if (!students.length) {
        toast.error("No students found", { position: "top-right" });
        return;
      }
      await DownloadAdmitCard(students, data?.admitCardDetail?.[0] || null);
      const close = document.querySelector('[data-bs-dismiss="modal"]');
      if (close) close.click();
      setModalClass("");
    } catch {
      toast.error("Something went wrong", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Admit Card — Class + Section Wise PDF ─────────────────────────────────
  const handleDownloadAdmitCardSectionWise = async () => {
    if (!modalClass || !modalSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    try {
      setIsDownloading(true);
      let url = `${server}/GetAdmitCardByClassAndSection?class=${modalClass}&section=${modalSection}`;
      if (modalMonth !== "All") url += `&month=${modalMonth}`;
      const res = await fetch(url);
      const data = await res.json();
      const students = (data?.allStudents || []).sort(
        (a, b) =>
          Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
      );
      if (!students.length) {
        toast.error("No students found", { position: "top-right" });
        return;
      }
      await DownloadAdmitCard(students, data?.admitCardDetail?.[0] || null);
      const close = document.querySelector('[data-bs-dismiss="modal"]');
      if (close) close.click();
      setModalClass("");
      setModalSection("");
    } catch {
      toast.error("Something went wrong", { position: "top-right" });
    } finally {
      setIsDownloading(false);
    }
  };

  // shared modal props
  const modalSharedProps = {
    isDownloading,
    selectedClass: modalClass,
    selectedSection: modalSection,
    setSelectedClass: setModalClass,
    setSelectedSection: setModalSection,
    selectedMonth: modalMonth,
    setSelectedMonth: setModalMonth,
  };

  // ── initial fetches ────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchSyllabus());
    dispatch(fetchSamplePapers());
  }, [dispatch]);

  // Marks – re-fetch when class / section / examType changes
  useEffect(() => {
    const cls = selectedClass || "II";
    const sec = selectedSection || "A";
    setLoadingMarks(true);
    fetch(
      `${server}/GetStudentMarksThroughFilter?session=${SESSION}&class=${cls}&section=${sec}&examType=${encodeURIComponent(selectedExamType)}`,
      { headers: { Authorization: localStorage.getItem("token") } },
    )
      .then((r) => r.json())
      .then((d) => setMarksData(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setLoadingMarks(false));
  }, [selectedClass, selectedSection, selectedExamType]);

  // Results – same filter set
  useEffect(() => {
    const cls = selectedClass || "II";
    const sec = selectedSection || "A";
    setLoadingResults(true);
    fetch(
      `${server}/GetStudentMarksThroughFilter?session=${SESSION}&examType=${encodeURIComponent(selectedExamType)}&class=${cls}&section=${sec}`,
      { headers: { Authorization: localStorage.getItem("token") } },
    )
      .then((r) => r.json())
      .then((d) => setResultsData(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setLoadingResults(false));
  }, [selectedClass, selectedSection, selectedExamType]);

  // Toppers
  useEffect(() => {
    const cls = selectedClass || "II";
    setLoadingToppers(true);
    fetch(`${server}/FilterToppers`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        topperType: "class",
        examType: selectedExamType,
        class: cls,
        session: SESSION,
        limit: 20,
      }),
    })
      .then((r) => r.json())
      .then((d) => setToppersData(Array.isArray(d) ? d : []))
      .catch(console.error)
      .finally(() => setLoadingToppers(false));
  }, [selectedClass, selectedExamType]);

  // Admit cards
  useEffect(() => {
    const cls = selectedClass || "II";
    setLoadingAdmitCards(true);
    fetch(`${server}/GetAdmitCardByClassAndSection?class=${cls}`, {
      headers: { Authorization: localStorage.getItem("token") },
    })
      .then((r) => r.json())
      .then((d) => setAdmitCardsData(d?.allStudents || []))
      .catch(console.error)
      .finally(() => setLoadingAdmitCards(false));
  }, [selectedClass]);

  // ── dataset map ────────────────────────────────────────────────────────────
  const datasetMap = {
    marks: { data: marksData, loading: loadingMarks },
    results: { data: resultsData, loading: loadingResults },
    toppers: { data: toppersData, loading: loadingToppers },
    admitcards: { data: admitCardsData, loading: loadingAdmitCards },
    syllabus: { data: syllabus || [], loading: loadingSyllabus },
    samplepapers: { data: samplePapers || [], loading: loadingSamplePapers },
  };

  const currentRaw = datasetMap[activeDataset]?.data || [];
  const currentLoading = datasetMap[activeDataset]?.loading || false;

  // ── filtered data ──────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    let data = [...currentRaw];

    if (selectedSubject) {
      data = data.filter((r) => {
        const sub = r.subject || r.Subject || "";
        return sub.toLowerCase() === selectedSubject.toLowerCase();
      });
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      const flatten = (obj) => {
        const vals = [];
        for (const k in obj) {
          const v = obj[k];
          if (v && typeof v === "object") vals.push(...flatten(v));
          else if (typeof v === "string") vals.push(v);
        }
        return vals;
      };
      data = data.filter((r) =>
        flatten(r).some((v) => v.toLowerCase().includes(lower)),
      );
    }

    return data;
  }, [currentRaw, selectedSubject, searchTerm]);

  // ── live snapshot ──────────────────────────────────────────────────────────
  const snapshot = useMemo(() => {
    const total = filteredData.length;
    let passed = 0,
      failed = 0,
      topperCount = 0;
    let totalPct = 0,
      pctCount = 0;

    filteredData.forEach((r) => {
      // Results / Marks
      if (r.percentage !== undefined) {
        const pct = parseFloat(r.percentage);
        if (!isNaN(pct)) {
          totalPct += pct;
          pctCount++;
        }
        if (pct >= 33) passed++;
        else failed++;
      }
      // Toppers flag
      if (r.totalMarks !== undefined) topperCount++;
    });

    const avgPct = pctCount > 0 ? Math.round(totalPct / pctCount) : null;
    return { total, passed, failed, topperCount, avgPct };
  }, [filteredData]);

  // ── select styles (identical to StudentReports) ────────────────────────────
  const selectStyles = {
    control: (b) => ({
      ...b,
      border: "none",
      boxShadow: "none",
      backgroundColor: "transparent",
      minHeight: "32px",
      cursor: "pointer",
    }),
    valueContainer: (b) => ({ ...b, padding: "0" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: "4px" }),
    menu: (b) => ({
      ...b,
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      border: "none",
      zIndex: 5,
    }),
    option: (b, s) => ({
      ...b,
      backgroundColor: s.isSelected
        ? "#ffc107"
        : s.isFocused
          ? "#f8f9fa"
          : "white",
      color: s.isSelected ? "white" : "#212529",
      cursor: "pointer",
      fontSize: "14px",
    }),
    placeholder: (b) => ({ ...b, color: "#6c757d", fontSize: "14px" }),
    singleValue: (b) => ({ ...b, color: "#212529", fontSize: "14px" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ROW FORMATTERS
  // ─────────────────────────────────────────────────────────────────────────
  const formatMarksRow = (r) => {
    const base = {
      "ADMISSION NO": toUpper(r.admission_no || ""),
      CLASS: toUpper(r.class || ""),
      SECTION: toUpper(r.section || ""),
      SESSION: toUpper(r.session || ""),
      "EXAM TYPE": toUpper(selectedExamType),
    };
    // flatten half-yearly subjects
    const subjects = r.result?.[selectedExamType] || [];
    subjects.forEach((sub) => {
      const prefix = toUpper(sub.subject);
      Object.entries(sub.marks || {}).forEach(([head, val]) => {
        base[`${prefix} - ${toUpper(head)}`] = String(val ?? "");
      });
    });
    return base;
  };

  const formatTopperRow = (r) => ({
    "ADMISSION NO": toUpper(r.admission_no || ""),
    NAME: toUpper(r.name || ""),
    CLASS: toUpper(r.class || ""),
    SECTION: toUpper(r.section || ""),
    "ROLL NO": toUpper(r.roll_no || ""),
    "TOTAL MARKS": String(r.totalMarks ?? ""),
    "MAX MARKS": String(r.maxMarks ?? ""),
    PERCENTAGE: r.maxMarks
      ? `${Math.round((r.totalMarks / r.maxMarks) * 100)}%`
      : "",
  });

  const formatAdmitRow = (r) => ({
    "ADMISSION NO": toUpper(r.studentDetails?.admission_no || ""),
    NAME: toUpper(r.studentDetails?.name || ""),
    CLASS: toUpper(r.currentClass || ""),
    SECTION: toUpper(r.studentDetails?.currentSection || ""),
    "ROLL NO": toUpper(r.studentDetails?.rollNo || ""),
    "DATE OF BIRTH": toUpper(r.studentDetails?.dateOfBirth || ""),
    GENDER: toUpper(r.studentDetails?.gender || ""),
  });

  const formatSyllabusRow = (r) => ({
    CLASS: toUpper(r.Class || r.class || ""),
    SUBJECT: toUpper(r.subject || ""),
    CATEGORY: toUpper(r.category || ""),
    SESSION: toUpper(r.session || ""),
    "FILE URL": r.fileUrl || r.file || "",
    "UPLOADED BY": toUpper(r.uploadedBy || ""),
  });

  const formatSamplePaperRow = (r) => ({
    TITLE: toUpper(r.title || ""),
    CLASS: toUpper(r.Class || r.class || ""),
    SUBJECT: toUpper(r.subject || ""),
    SESSION: toUpper(r.session || ""),
    "FILE URL": r.fileUrl || r.file || "",
    "UPLOADED BY": toUpper(r.uploadedBy || ""),
  });

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — MARKS
  // ─────────────────────────────────────────────────────────────────────────
  const reportMarksAll = () => {
    if (!marksData.length) {
      toast.error("No marks data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = marksData.map(formatMarksRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ALL MARKS",
    );
    writeWorkbook(wb, `ExamMarks_All_${today}.xlsx`);
  };

  const reportMarksClassWise = () => {
    if (!marksData.length) {
      toast.error("No marks data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    marksData.forEach((r) => {
      const key = `${r.class || "?"}-${r.section || "?"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatMarksRow(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(key).slice(0, 31),
      );
    });
    writeWorkbook(wb, `ExamMarks_ClassWise_${today}.xlsx`);
  };

  const reportMarksByExamType = (examType) => () => {
    if (!marksData.length) {
      toast.error("No marks data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = marksData.map(formatMarksRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      toUpper(examType).slice(0, 31),
    );
    writeWorkbook(wb, `ExamMarks_${examType.replace(/ /g, "_")}_${today}.xlsx`);
  };

  const reportMarksSubjectWise = () => {
    if (!marksData.length) {
      toast.error("No marks data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    marksData.forEach((r) => {
      const subjects = r.result?.[selectedExamType] || [];
      subjects.forEach((sub) => {
        const key = toUpper(sub.subject || "UNKNOWN");
        if (!groups[key]) groups[key] = [];
        const row = {
          "ADMISSION NO": toUpper(r.admission_no || ""),
          CLASS: toUpper(r.class || ""),
          SECTION: toUpper(r.section || ""),
        };
        Object.entries(sub.marks || {}).forEach(([h, v]) => {
          row[toUpper(h)] = String(v ?? "");
        });
        groups[key].push(row);
      });
    });
    Object.entries(groups).forEach(([sub, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        sub.slice(0, 31),
      );
    });
    writeWorkbook(wb, `ExamMarks_SubjectWise_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — RESULTS
  // ─────────────────────────────────────────────────────────────────────────
  const reportResultsAll = () => {
    if (!resultsData.length) {
      toast.error("No results data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = resultsData.map(formatMarksRow);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ALL RESULTS",
    );
    writeWorkbook(wb, `Results_All_${today}.xlsx`);
  };

  const reportResultsClassWise = () => {
    if (!resultsData.length) {
      toast.error("No results data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    resultsData.forEach((r) => {
      const cls = r.class || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatMarksRow(r));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Results_ClassWise_${today}.xlsx`);
  };

  const reportResultsSectionWise = () => {
    if (!resultsData.length) {
      toast.error("No results data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    resultsData.forEach((r) => {
      const key = `${r.class || "?"} ${r.section || "?"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatMarksRow(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(key).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Results_SectionWise_${today}.xlsx`);
  };

  const reportResultsHalfYearly = () => {
    if (!resultsData.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(resultsData.map(formatMarksRow)),
      "HALF YEARLY",
    );
    writeWorkbook(wb, `Results_HalfYearly_${today}.xlsx`);
  };

  const reportResultsAnnual = () => {
    if (!resultsData.length) {
      toast.error("No data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(resultsData.map(formatMarksRow)),
      "ANNUAL",
    );
    writeWorkbook(wb, `Results_Annual_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — TOPPERS
  // ─────────────────────────────────────────────────────────────────────────
  const reportToppersAll = () => {
    if (!toppersData.length) {
      toast.error("No toppers data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = toppersData.map(formatTopperRow);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "TOPPERS");
    writeWorkbook(wb, `Toppers_All_${today}.xlsx`);
  };

  const reportToppersClassWise = () => {
    if (!toppersData.length) {
      toast.error("No toppers data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    toppersData.forEach((r) => {
      const cls = r.class || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatTopperRow(r));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      rows.sort((a, b) => Number(b["TOTAL MARKS"]) - Number(a["TOTAL MARKS"]));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `Toppers_ClassWise_${today}.xlsx`);
  };

  const reportToppersSubjectWise = () => {
    if (!toppersData.length) {
      toast.error("No toppers data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    toppersData.forEach((r) => {
      (r.subjects || []).forEach((sub) => {
        const key = toUpper(sub.subject || sub || "UNKNOWN");
        if (!groups[key]) groups[key] = [];
        groups[key].push({
          "ADMISSION NO": toUpper(r.admission_no || ""),
          NAME: toUpper(r.name || ""),
          CLASS: toUpper(r.class || ""),
          SECTION: toUpper(r.section || ""),
          SUBJECT: key,
          MARKS: String(sub.total ?? ""),
        });
      });
    });
    Object.entries(groups).forEach(([sub, rows]) => {
      rows.sort((a, b) => Number(b.MARKS) - Number(a.MARKS));
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        sub.slice(0, 31),
      );
    });
    writeWorkbook(wb, `Toppers_SubjectWise_${today}.xlsx`);
  };

  const reportToppersTop5 = () => {
    const sorted = [...toppersData]
      .sort((a, b) => (b.totalMarks || 0) - (a.totalMarks || 0))
      .slice(0, 5);
    if (!sorted.length) {
      toast.error("No toppers data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(sorted.map(formatTopperRow)),
      "TOP 5",
    );
    writeWorkbook(wb, `Toppers_Top5_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — ADMIT CARDS
  // ─────────────────────────────────────────────────────────────────────────
  const reportAdmitCardsAll = () => {
    if (!admitCardsData.length) {
      toast.error("No admit card data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const rows = admitCardsData.map(formatAdmitRow);
    sortByRoll(rows);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "ADMIT CARDS",
    );
    writeWorkbook(wb, `AdmitCards_All_${today}.xlsx`);
  };

  const reportAdmitCardsClassWise = () => {
    if (!admitCardsData.length) {
      toast.error("No admit card data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    admitCardsData.forEach((r) => {
      const cls = r.currentClass || "UNKNOWN";
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatAdmitRow(r));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(cls).slice(0, 31),
      );
    });
    writeWorkbook(wb, `AdmitCards_ClassWise_${today}.xlsx`);
  };

  const reportAdmitCardsSectionWise = () => {
    if (!admitCardsData.length) {
      toast.error("No admit card data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    admitCardsData.forEach((r) => {
      const key = `${r.currentClass || "?"}-${r.studentDetails?.currentSection || "?"}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(formatAdmitRow(r));
    });
    Object.entries(groups).forEach(([key, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        toUpper(key).slice(0, 31),
      );
    });
    writeWorkbook(wb, `AdmitCards_SectionWise_${today}.xlsx`);
  };

  const reportAdmitCardsGenderWise = () => {
    if (!admitCardsData.length) {
      toast.error("No admit card data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    admitCardsData.forEach((r) => {
      const g = toUpper(r.studentDetails?.gender || "UNKNOWN");
      if (!groups[g]) groups[g] = [];
      groups[g].push(formatAdmitRow(r));
    });
    Object.entries(groups).forEach(([g, rows]) => {
      sortByRoll(rows);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        g.slice(0, 31),
      );
    });
    writeWorkbook(wb, `AdmitCards_GenderWise_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — SYLLABUS
  // ─────────────────────────────────────────────────────────────────────────
  const syllabusArr = syllabus || [];

  const reportSyllabusAll = () => {
    if (!syllabusArr.length) {
      toast.error("No syllabus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(syllabusArr.map(formatSyllabusRow)),
      "SYLLABUS",
    );
    writeWorkbook(wb, `Syllabus_All_${today}.xlsx`);
  };

  const reportSyllabusClassWise = () => {
    if (!syllabusArr.length) {
      toast.error("No syllabus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    syllabusArr.forEach((r) => {
      const cls = toUpper(r.Class || r.class || "UNKNOWN");
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatSyllabusRow(r));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        cls.slice(0, 31),
      );
    });
    writeWorkbook(wb, `Syllabus_ClassWise_${today}.xlsx`);
  };

  const reportSyllabusSubjectWise = () => {
    if (!syllabusArr.length) {
      toast.error("No syllabus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    syllabusArr.forEach((r) => {
      const sub = toUpper(r.subject || "UNKNOWN");
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(formatSyllabusRow(r));
    });
    Object.entries(groups).forEach(([sub, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        sub.slice(0, 31),
      );
    });
    writeWorkbook(wb, `Syllabus_SubjectWise_${today}.xlsx`);
  };

  const reportSyllabusCategoryWise = () => {
    if (!syllabusArr.length) {
      toast.error("No syllabus data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    syllabusArr.forEach((r) => {
      const cat = toUpper(r.category || "UNKNOWN");
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(formatSyllabusRow(r));
    });
    Object.entries(groups).forEach(([cat, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        cat.slice(0, 31),
      );
    });
    writeWorkbook(wb, `Syllabus_CategoryWise_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT GENERATORS — SAMPLE PAPERS
  // ─────────────────────────────────────────────────────────────────────────
  const papersArr = samplePapers || [];

  const reportSamplePapersAll = () => {
    if (!papersArr.length) {
      toast.error("No sample paper data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(papersArr.map(formatSamplePaperRow)),
      "SAMPLE PAPERS",
    );
    writeWorkbook(wb, `SamplePapers_All_${today}.xlsx`);
  };

  const reportSamplePapersClassWise = () => {
    if (!papersArr.length) {
      toast.error("No sample paper data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    papersArr.forEach((r) => {
      const cls = toUpper(r.Class || r.class || "UNKNOWN");
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(formatSamplePaperRow(r));
    });
    Object.entries(groups).forEach(([cls, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        cls.slice(0, 31),
      );
    });
    writeWorkbook(wb, `SamplePapers_ClassWise_${today}.xlsx`);
  };

  const reportSamplePapersSubjectWise = () => {
    if (!papersArr.length) {
      toast.error("No sample paper data");
      return;
    }
    const wb = XLSX.utils.book_new();
    const groups = {};
    papersArr.forEach((r) => {
      const sub = toUpper(r.subject || "UNKNOWN");
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(formatSamplePaperRow(r));
    });
    Object.entries(groups).forEach(([sub, rows]) => {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(rows),
        sub.slice(0, 31),
      );
    });
    writeWorkbook(wb, `SamplePapers_SubjectWise_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────
  const reportExamSummary = () => {
    const all = [...marksData];
    if (!all.length) {
      toast.error("No marks data");
      return;
    }
    const groups = {};
    all.forEach((r) => {
      const key = `${r.class || "?"}||${r.section || "?"}`;
      if (!groups[key])
        groups[key] = {
          Class: r.class,
          Section: r.section,
          Total: 0,
          SubjectCount: 0,
        };
      groups[key].Total++;
      groups[key].SubjectCount += (r.result?.[selectedExamType] || []).length;
    });
    const rows = Object.values(groups);
    const totals = rows.reduce(
      (acc, r) => {
        acc.Total += r.Total;
        acc.SubjectCount += r.SubjectCount;
        return acc;
      },
      { Class: "TOTAL", Section: "", Total: 0, SubjectCount: 0 },
    );
    rows.push(totals);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Exam Summary",
    );
    writeWorkbook(wb, `ExamSummary_${today}.xlsx`);
  };

  const reportMasterCombined = () => {
    const master = [];
    marksData.forEach((r) => {
      const row = formatMarksRow(r);
      row["TYPE"] = "MARKS";
      master.push(row);
    });
    toppersData.forEach((r) => {
      const row = formatTopperRow(r);
      row["TYPE"] = "TOPPER";
      master.push(row);
    });
    if (!master.length) {
      toast.error("No data available");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(master),
      "MASTER",
    );
    writeWorkbook(wb, `ExamMasterReport_${today}.xlsx`);
  };

  const reportAcademicDocSummary = () => {
    const rows = [];
    syllabusArr.forEach((r) =>
      rows.push({
        TYPE: "SYLLABUS",
        CLASS: toUpper(r.Class || r.class || ""),
        SUBJECT: toUpper(r.subject || ""),
        CATEGORY: toUpper(r.category || ""),
      }),
    );
    papersArr.forEach((r) =>
      rows.push({
        TYPE: "SAMPLE PAPER",
        CLASS: toUpper(r.Class || r.class || ""),
        SUBJECT: toUpper(r.subject || ""),
        TITLE: toUpper(r.title || ""),
      }),
    );
    if (!rows.length) {
      toast.error("No academic docs data");
      return;
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(rows),
      "Academic Docs",
    );
    writeWorkbook(wb, `AcademicDocs_Summary_${today}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  REPORT DEFINITIONS LIST
  // ─────────────────────────────────────────────────────────────────────────
  const reports = [
    // Marks
    { cat: "marks", label: "All Marks", action: reportMarksAll },
    {
      cat: "marks",
      label: "Class / Section Wise",
      action: reportMarksClassWise,
    },
    { cat: "marks", label: "Subject Wise", action: reportMarksSubjectWise },
    {
      cat: "marks",
      label: "Half Yearly Marks",
      action: reportMarksByExamType("half yearly"),
    },
    {
      cat: "marks",
      label: "Annual Marks",
      action: reportMarksByExamType("annual"),
    },
    {
      cat: "marks",
      label: "Unit Test Marks",
      action: reportMarksByExamType("unit test"),
    },
    // Results
    { cat: "results", label: "All Results", action: reportResultsAll },
    { cat: "results", label: "Class Wise", action: reportResultsClassWise },
    { cat: "results", label: "Section Wise", action: reportResultsSectionWise },
    {
      cat: "results",
      label: "Half Yearly Result",
      action: reportResultsHalfYearly,
    },
    { cat: "results", label: "Annual Result", action: reportResultsAnnual },
    // Toppers
    { cat: "toppers", label: "All Toppers", action: reportToppersAll },
    {
      cat: "toppers",
      label: "Class Wise Toppers",
      action: reportToppersClassWise,
    },
    {
      cat: "toppers",
      label: "Subject Wise Toppers",
      action: reportToppersSubjectWise,
    },
    { cat: "toppers", label: "Top 5 Overall", action: reportToppersTop5 },
    // Admit Cards
    {
      cat: "admitcards",
      label: "All Admit Cards",
      action: reportAdmitCardsAll,
    },
    {
      cat: "admitcards",
      label: "Class Wise",
      action: reportAdmitCardsClassWise,
    },
    {
      cat: "admitcards",
      label: "Section Wise",
      action: reportAdmitCardsSectionWise,
    },
    {
      cat: "admitcards",
      label: "Gender Wise",
      action: reportAdmitCardsGenderWise,
    },
    // Academic Docs
    { cat: "academic", label: "All Syllabus", action: reportSyllabusAll },
    {
      cat: "academic",
      label: "Syllabus — Class Wise",
      action: reportSyllabusClassWise,
    },
    {
      cat: "academic",
      label: "Syllabus — Subject Wise",
      action: reportSyllabusSubjectWise,
    },
    {
      cat: "academic",
      label: "Syllabus — Category Wise",
      action: reportSyllabusCategoryWise,
    },
    {
      cat: "academic",
      label: "All Sample Papers",
      action: reportSamplePapersAll,
    },
    {
      cat: "academic",
      label: "Papers — Class Wise",
      action: reportSamplePapersClassWise,
    },
    {
      cat: "academic",
      label: "Papers — Subject Wise",
      action: reportSamplePapersSubjectWise,
    },
    // Analytics
    { cat: "analytics", label: "Exam Summary", action: reportExamSummary },
    {
      cat: "analytics",
      label: "Academic Docs Summary",
      action: reportAcademicDocSummary,
    },
    {
      cat: "analytics",
      label: "Master Combined Report",
      action: reportMasterCombined,
    },
  ];

  const visibleReports =
    activeCategory === "all"
      ? reports
      : reports.filter((r) => r.cat === activeCategory);

  // ── option arrays ──────────────────────────────────────────────────────────
  const classOptions = classList.map((c) => ({ value: c, label: c }));
  const sectionOptions = sectionList.map((s) => ({ value: s, label: s }));
  const subjectOptions = subjectList.map((s) => ({ value: s, label: s }));
  const examOptions = EXAM_TYPES.map((e) => ({
    value: e,
    label: e.replace(/\b\w/g, (c) => c.toUpperCase()),
  }));

  // ── quick-export for active dataset ───────────────────────────────────────
  const quickExport = () => {
    switch (activeDataset) {
      case "marks":
        return reportMarksAll();
      case "results":
        return reportResultsAll();
      case "toppers":
        return reportToppersAll();
      case "admitcards":
        return reportAdmitCardsAll();
      case "syllabus":
        return reportSyllabusAll();
      case "samplepapers":
        return reportSamplePapersAll();
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="container-fluid my-3 px-3">
            <div className="row g-2">
              {/* ══════════════ LEFT COLUMN ══════════════ */}
              <div className="col-lg-8">
                {/* ── HERO HEADER ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="bg-light rounded-4 p-4 position-relative overflow-hidden">
                    <div className="d-flex align-items-center">
                      <p className="badge bg-white text-dark rounded-pill px-4 py-2 mb-0 fs-6 fw-medium">
                        <i className="bi bi-journal-check me-1"></i> Reports
                        &amp; Analytics
                      </p>
                      <div className="ms-4">
                        <h3 className="fw-bold report-title m-0 lh-sm text-dark">
                          Examination Insights Hub
                        </h3>
                        <p className="text-secondary fs-6 fw-medium mb-0 lh-sm">
                          Generate, filter &amp; download every examination
                          report — all in one place.
                        </p>
                      </div>
                    </div>

                    {/* DATASET SELECTOR */}
                    <div className="mt-4">
                      <div className="row g-1">
                        {Object.entries(DATASETS).map(([key, ds]) => {
                          const isActive = activeDataset === key;
                          const count = datasetMap[key]?.data?.length || 0;
                          return (
                            <div className="col-md-3 col-6" key={key}>
                              <div
                                onClick={() => setActiveDataset(key)}
                                className={`p-3 rounded-4 cursor-pointer position-relative ${
                                  isActive
                                    ? `bg-${ds.color} text-white`
                                    : `bg-${ds.color}-soft`
                                }`}
                              >
                                {isActive && (
                                  <span className="position-absolute top-0 end-0 m-2">
                                    <i className="bi bi-check-circle-fill text-white fs-5"></i>
                                  </span>
                                )}
                                <p
                                  className={`m-0 fw-normal fs-5 lh-sm ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {ds.label}
                                </p>
                                <h3
                                  className={`fw-bold m-0 lh-1 mt-1 ${isActive ? "text-white" : "text-dark"}`}
                                >
                                  {count.toLocaleString("en-IN")}
                                </h3>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── FILTERS ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-funnel-fill me-2 text-secondary"></i>
                      Refine Results
                    </p>
                    <span
                      onClick={() => {
                        setSelectedClass("");
                        setSelectedSection("");
                        setSelectedSubject("");
                        setFromDate("");
                        setToDate("");
                        setSearchTerm("");
                        setSelectedExamType("half yearly");
                      }}
                      className="badge bg-danger-soft text-dark rounded-3 px-3 py-2 cursor-pointer fw-medium"
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>{" "}
                      Reset
                    </span>
                  </div>

                  {/* Search */}
                  <div className="bg-light rounded-4 mb-3">
                    <form
                      className="d-flex align-items-center"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <span className="position-absolute ps-3 search-icon">
                        <i className="bi bi-filter-circle-fill ms-1" />
                      </span>
                      <input
                        type="search"
                        className="form-control ps-7 rounded-4 border-0 bg-light"
                        placeholder="Search admission no, name, subject..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </form>
                  </div>

                  {/* Class, Section & Exam Type */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">
                      Class, Section &amp; Exam Type
                    </p>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-mortarboard-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            placeholder="All Classes"
                            value={
                              selectedClass
                                ? { value: selectedClass, label: selectedClass }
                                : null
                            }
                            onChange={(opt) => {
                              setSelectedClass(opt ? opt.value : "");
                              setSelectedSection("");
                            }}
                            options={classOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-bookmark-fill text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            isClearable
                            isDisabled={!selectedClass}
                            placeholder={
                              selectedClass
                                ? "All Sections"
                                : "Select class first"
                            }
                            value={
                              selectedSection
                                ? {
                                    value: selectedSection,
                                    label: selectedSection,
                                  }
                                : null
                            }
                            onChange={(opt) =>
                              setSelectedSection(opt ? opt.value : "")
                            }
                            options={sectionOptions}
                            styles={selectStyles}
                            noOptionsMessage={() => "No sections"}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center h-100">
                          <i className="bi bi-journal-text text-secondary me-2"></i>
                          <Select
                            className="flex-grow-1"
                            placeholder="Exam Type"
                            value={{
                              value: selectedExamType,
                              label: selectedExamType.replace(/\b\w/g, (c) =>
                                c.toUpperCase(),
                              ),
                            }}
                            onChange={(opt) =>
                              setSelectedExamType(
                                opt ? opt.value : "half yearly",
                              )
                            }
                            options={examOptions}
                            styles={selectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subject filter */}
                  <div className="bg-light rounded-4 py-3 px-4 mb-2">
                    <p className="m-0 fw-medium lh-1 text-dark mb-2">Subject</p>
                    <div className="bg-white rounded-3 px-3 py-1 d-flex align-items-center">
                      <i className="bi bi-book-fill text-secondary me-2"></i>
                      <Select
                        className="flex-grow-1"
                        isClearable
                        placeholder="All Subjects"
                        value={
                          selectedSubject
                            ? { value: selectedSubject, label: selectedSubject }
                            : null
                        }
                        onChange={(opt) =>
                          setSelectedSubject(opt ? opt.value : "")
                        }
                        options={subjectOptions}
                        styles={selectStyles}
                      />
                    </div>
                  </div>
                </div>

                {/* ── LIVE SNAPSHOT ── */}
                <div className="bg-white rounded-4 p-4 mb-2">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="m-0 fw-medium lh-1 text-dark fs-5">
                      <i className="bi bi-pie-chart-fill me-2 text-secondary"></i>
                      Live Snapshot
                    </p>
                    <span className="badge bg-success-soft text-dark rounded-3 px-4 py-2 fw-medium">
                      <i className="bi bi-broadcast me-1"></i> Real-time
                    </span>
                  </div>

                  <div className="row g-2">
                    {/* Donut */}
                    <div className="col-md-5">
                      <div className="bg-danger-soft rounded-4 p-4 h-100 d-flex flex-column align-items-center justify-content-center position-relative">
                        <svg viewBox="0 0 100 100" className="w-75">
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="12"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#198754"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.passed / snapshot.total) * 251.2 : 0} 251.2`}
                            transform="rotate(-90 50 50)"
                          />
                          <circle
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#dc3545"
                            strokeWidth="12"
                            strokeDasharray={`${snapshot.total ? (snapshot.failed / snapshot.total) * 251.2 : 0} 251.2`}
                            strokeDashoffset={`${snapshot.total ? -(snapshot.passed / snapshot.total) * 251.2 : 0}`}
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="position-absolute text-center">
                          <p className="m-0 fs-6 text-secondary lh-1">
                            Records
                          </p>
                          <h2 className="m-0 fw-bold text-dark lh-1 mt-1">
                            {snapshot.total.toLocaleString("en-IN")}
                          </h2>
                        </div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="col-md-7">
                      <div className="row g-1 h-100">
                        <div className="col-6">
                          <div className="bg-success-soft rounded-4 p-4 h-100">
                            <i className="bi bi-patch-check-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Passed
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.passed.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.passed / snapshot.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-danger-soft rounded-4 p-4 h-100">
                            <i className="bi bi-x-circle-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Failed
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {snapshot.failed.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {snapshot.total
                                ? Math.round(
                                    (snapshot.failed / snapshot.total) * 100,
                                  )
                                : 0}
                              % of total
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-info-soft rounded-4 p-4 h-100">
                            <i className="bi bi-trophy-fill text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Toppers
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {toppersData.length.toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              Ranked records
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="bg-dark-soft rounded-4 p-4 h-100">
                            <i className="bi bi-file-earmark-text text-dark fs-3 d-block lh-1"></i>
                            <p className="m-0 fs-6 text-secondary lh-1 mt-2">
                              Academic Docs
                            </p>
                            <h4 className="m-0 fw-bold text-dark">
                              {(
                                syllabusArr.length + papersArr.length
                              ).toLocaleString("en-IN")}
                            </h4>
                            <p className="m-0 fs-6 text-secondary mt-1 lh-1">
                              {syllabusArr.length} syllabus · {papersArr.length}{" "}
                              papers
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ══════════════ RIGHT COLUMN ══════════════ */}
              <div className="col-lg-4">
                <div className="mb-3">
                  <div className="mt-2 ms-4">
                    <nav aria-label="breadcrumb">
                      <ol className="breadcrumb pe-3">
                        <li className="breadcrumb-item">
                          <a href="#">Reports &amp; Analytics</a>
                        </li>
                        <li
                          className="breadcrumb-item active"
                          aria-current="page"
                        >
                          Examination
                        </li>
                      </ol>
                    </nav>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-4">
                  {/* Avatar + count */}
                  <div className="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                    <div className="avatar-group">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <span className="avatar avatar-md" key={num}>
                          <img
                            alt={`avatar-${num}`}
                            src={`/assets/images/students/${num}.jpg`}
                            className="rounded-circle"
                          />
                        </span>
                      ))}
                    </div>
                    <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                      {filteredData.length.toLocaleString("en-IN")}
                    </h3>
                  </div>

                  {/* Title row */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      {currentLoading ? (
                        <div
                          className="spinner-border text-dark data-loader-spinner"
                          role="status"
                        ></div>
                      ) : (
                        <img src="/assets/images/folder.svg" alt="" />
                      )}
                      <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                        {DATASETS[activeDataset]?.label}
                        <br />
                        <span className="text-secondary fs-6 fw-medium lh-sm">
                          {reports.length} Reports Available
                        </span>
                      </p>
                    </div>
                    <span
                      onClick={quickExport}
                      className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                    >
                      Export
                    </span>
                  </div>

                  {/* Category tabs */}
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {REPORT_CATEGORIES.map((cat) => (
                      <div
                        key={cat.key}
                        className={`bg-dark-soft rounded-3 cursor-pointer px-3 py-1 fs-6 ${
                          activeCategory === cat.key
                            ? "bg-warning text-white border-warning"
                            : ""
                        }`}
                        onClick={() => setActiveCategory(cat.key)}
                      >
                        {cat.label}
                      </div>
                    ))}
                  </div>

                  {/* Report buttons */}
                  <div className="px-4 py-2 mt-3 bg-light rounded-4">
                    <li className="list-group-item px-0">
                      <a
                        className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                        data-bs-toggle="collapse"
                        href="#examReportsCollapse"
                        role="button"
                        aria-expanded="true"
                        aria-controls="examReportsCollapse"
                      >
                        <p className="m-0 fw-medium lh-sm text-dark">
                          Available Reports ({visibleReports.length})
                        </p>
                        <span className="chevron-arrow ms-4">
                          <i className="bi bi-chevron-down fs-4" />
                        </span>
                      </a>
                      <div className="collapse" id="examReportsCollapse">
                        <div className="py-3">
                          <div className="rounded-4 d-flex flex-wrap">
                            {visibleReports.map((r, i) => (
                              <span
                                key={i}
                                onClick={r.action}
                                className="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  </div>

                  {/* Quick action cards — Results */}
                  <div className="mt-2">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          onClick={reportResultsHalfYearly}
                          className="p-3 bg-success-soft rounded-4 cursor-pointer h-100"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Half Yearly
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (XLS)
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportResultsAnnual}
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer h-100"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Annual
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (XLS)
                          </p>
                        </div>
                      </div>
                      <div className="col-12">
                        <div
                          onClick={reportMasterCombined}
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Master Combined Report
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Marks + Toppers in One Sheet
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportToppersTop5}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Toppers
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Top 5 Overall
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportToppersClassWise}
                          className="p-3 bg-warning-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Toppers
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Class Wise
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportSyllabusAll}
                          className="p-3 bg-dark-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Syllabus
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Records
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          onClick={reportSamplePapersAll}
                          className="p-3 bg-dark-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Sample Papers
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            All Records
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-center">
                    <hr className="w-50 mb-0" />
                  </div>

                  {/* ── PDF / XLS Download Cards ── */}
                  <div className="mt-3">
                    <div className="row g-1">
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#halfYearlyPDFModal"
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Half Yearly
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (PDF)
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#halfYearlyXLSModal"
                          className="p-3 bg-success-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Half Yearly
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (XLS)
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#annualPDFModal"
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Annual
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (PDF)
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#annualXLSModal"
                          className="p-3 bg-danger-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Annual
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Result (XLS)
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#admitCardClassWiseModal"
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Admit Card
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Download Class Wise
                          </p>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          data-bs-toggle="modal"
                          data-bs-target="#admitCardSectionWiseModal"
                          className="p-3 bg-info-soft rounded-4 cursor-pointer"
                        >
                          <p className="m-0 text-center fw-medium lh-sm text-dark">
                            Admit Card
                          </p>
                          <p className="text-center fs-5 m-0 lh-1">
                            Download Section Wise
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* end right col */}
            </div>
          </div>

          {/* ══════════════ MODALS ══════════════ */}

          {/* Half Yearly PDF */}
          <ExamModal
            id="halfYearlyPDFModal"
            title="Half Yearly Examination Result"
            subtitle="Generate PDF Report Card"
            onDownload={handleDownloadHalfYearlyPDF}
            {...modalSharedProps}
          />

          {/* Half Yearly XLS */}
          <ExamModal
            id="halfYearlyXLSModal"
            title="Half Yearly Examination Result"
            subtitle="Generate Excel Sheet"
            onDownload={handleDownloadHalfYearlyXLS}
            {...modalSharedProps}
          />

          {/* Annual PDF */}
          <ExamModal
            id="annualPDFModal"
            title="Annual Examination Result"
            subtitle="Generate PDF Report Card"
            onDownload={handleDownloadAnnualPDF}
            {...modalSharedProps}
          />

          {/* Annual XLS */}
          <ExamModal
            id="annualXLSModal"
            title="Annual Examination Result"
            subtitle="Generate Excel Sheet"
            onDownload={handleDownloadAnnualXLS}
            {...modalSharedProps}
          />

          {/* Admit Card — Class Wise */}
          <ExamModal
            id="admitCardClassWiseModal"
            title="Admit Card"
            subtitle="Class Wise PDF"
            showMonth={true}
            onDownload={handleDownloadAdmitCardClassWise}
            {...modalSharedProps}
          />

          {/* Admit Card — Section Wise */}
          <ExamModal
            id="admitCardSectionWiseModal"
            title="Admit Card"
            subtitle="Class / Section Wise PDF"
            showMonth={true}
            onDownload={handleDownloadAdmitCardSectionWise}
            {...modalSharedProps}
          />
        </main>
      </div>
    </>
  );
}

export default ExaminationReports;
